// UserWithdrawalForm.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { auth, db } from '../../firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';

const UserWithdrawalForm = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    amount: searchParams.get('amount') || '0.00',
  });

  const [withdrawalRequest, setWithdrawalRequest] = useState(null);
  const [status, setStatus] = useState({
    loading: true,
    error: null,
    success: false,
  });

  useEffect(() => {
    const validateAndLoadRequest = async () => {
      try {
        const requestId = searchParams.get('requestId');
        const token = searchParams.get('token');

        if (!requestId || !token) {
          throw new Error('Invalid withdrawal request');
        }

        // Verify the Firebase ID token
        await signInWithCustomToken(auth, token);

        // Load the withdrawal request
        const requestRef = doc(db, 'withdrawalRequests', requestId);
        const requestDoc = await getDoc(requestRef);

        if (!requestDoc.exists()) {
          throw new Error('Withdrawal request not found');
        }

        const requestData = requestDoc.data();

        // Verify request status and ownership
        if (requestData.status !== 'pending') {
          throw new Error('This withdrawal request has already been processed');
        }

        if (requestData.userId !== auth.currentUser?.uid) {
          throw new Error('Unauthorized access to withdrawal request');
        }

        setWithdrawalRequest({
          id: requestDoc.id,
          ...requestData,
        });

        setStatus({
          loading: false,
          error: null,
          success: false,
        });
      } catch (error) {
        console.error('Error validating withdrawal request:', error);
        setStatus({
          loading: false,
          error: error.message,
          success: false,
        });
      }
    };

    validateAndLoadRequest();
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName) {
      setStatus({
        loading: false,
        error: 'Please provide both first name and last name.',
        success: false,
      });
      return;
    }

    if (!withdrawalRequest) {
      return;
    }

    setStatus({ loading: true, error: null, success: false });

    try {
      // Update the withdrawal request in Firestore
      const requestRef = doc(db, 'withdrawalRequests', withdrawalRequest.id);
      await updateDoc(requestRef, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        status: 'submitted',
        submittedAt: new Date(),
        updatedAt: new Date(),
      });

      setStatus({
        loading: false,
        error: null,
        success: true,
      });
    } catch (error) {
      console.error('Error submitting withdrawal request:', error);
      setStatus({
        loading: false,
        error: 'Failed to submit withdrawal request. Please try again.',
        success: false,
      });
    }
  };

  if (status.loading) {
    return <div className="text-center p-4">Loading withdrawal request...</div>;
  }

  if (status.error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
        {status.error}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-md">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Withdrawal Request
          </h2>

          {status.success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 rounded-md">
              Your withdrawal request has been submitted successfully!
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={status.loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={status.loading}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-lg font-semibold text-gray-800">
                Withdrawal Amount: GHC {parseFloat(formData.amount).toFixed(2)}
              </p>
            </div>

            <button
              type="submit"
              disabled={status.loading}
              className="w-full px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              {status.loading ? 'Processing...' : 'Submit Withdrawal'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UserWithdrawalForm;
